import React, { Fragment } from 'react';
import { get } from 'lodash-es';
import { Col, Form, FormGroup, Input, Label, Row, UncontrolledTooltip } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const numbersOnlyText = '(numbers only, no decimals or symbols)';
const orderBidTooltipText = 'An absentee bid has been placed on this item. You can not change the starting bid or reserve amount.';

const LotForm = ({
  handleInputChange,
  hasOrderBids,
  isEditing = false,
  lotData: {
    artistName,
    circa,
    condition,
    dimensions,
    exhibited,
    hiEst,
    invNumber,
    itemPrice,
    literature,
    loEst,
    lotDescription,
    lotExt,
    lotNumber,
    hasLotNumber,
    lotTitle,
    medium,
    notes,
    provenance,
    reserve,
    shipHeight,
    shipLength,
    shipWeight,
    shipWidth,
    shipping,
    shippingIntl,
    startingBid
  },
  type,
  validateFields,
  validation,
  isGoldPlatinum,
}) => {
  let reserveClass = '';
  let startingBidClass = '';
  if (hasOrderBids) {
    reserveClass = 'cursor-not-allowed';
    startingBidClass = 'cursor-not-allowed';
  } else {
    if (get(validation, 'reserve')) {
      reserveClass = 'is-invalid';
    }
    if (get(validation, 'startingBid') || get(validation, 'itemPrice')) {
      startingBidClass = 'is-invalid';
    }
  }

  let lotNumberClass = '';
  let lotExtClass = '';

  if (isEditing && hasLotNumber) {
    lotNumberClass = 'cursor-not-allowed';
    lotExtClass = 'cursor-not-allowed';
  } else {
    if (get(validation, 'lotNumber')) {
      lotNumberClass = 'is-invalid';
    }

    if (get(validation, 'lotExt')) {
      lotExtClass = 'is-invalid';
    }
  }

  const isBin = type.toLowerCase() === 'bin';
  const priceLabel = isBin ? 'itemPrice' : 'startingBid';

  // For the four tooltips one above another that say what the label of the field is
  const shippingTooltipBeginText = 'This field is for the';
  const shippingTooltipEndText = 'of the package the item will be shipped in.';

  const validateLotNumber = () => get(validation, 'lotNumber');

  return (
    <Form>
      <Row>
        {/* Start Left Column */}
        <Col xs={6}>
          <h3>Item Details</h3>
          <FormGroup>
            <Label for="lotNumber">Lot Number*</Label>
            <Input
              id="lotNumber"
              className={lotNumberClass}
              disabled={hasLotNumber}
              name="lotNumber"
              onBlur={() => validateFields('lotNumber')}
              onChange={e => handleInputChange(e)}
              value={lotNumber}
            />
            {get(validation, 'lotNumber') && (
            <p className="color-danger my-0">{validateLotNumber()}</p>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="lotExt">Lot Extension</Label>
            <Input
              id="lotExt"
              className={lotExtClass}
              disabled={hasLotNumber}
              name="lotExt"
              onBlur={() => validateFields('lotExt')}
              onChange={e => handleInputChange(e)}
              value={lotExt}
            />
            {get(validation, 'lotExt') && (
            <p className="color-danger my-0">{get(validation, 'lotExt')}</p>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="invNumber">Inventory Number</Label>
            <Input
              id="invNumber"
              className={get(validation, 'invNumber') ? 'is-invalid' : ''}
              name="invNumber"
              onBlur={() => {
                validateFields('invNumber');
                validateFields('lotNumber');
              }}
              onChange={e => handleInputChange(e)}
              value={invNumber}
            />
            {get(validation, 'invNumber') && (
            <p className="color-danger my-0">{get(validation, 'invNumber')}</p>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="lotTitle">Item Title*</Label> (max. 250 characters, no HTML or JavaScript)
            <Input
              id="lotTitle"
              className={get(validation, 'lotTitle') ? 'is-invalid' : ''}
              name="lotTitle"
              onBlur={() => validateFields('lotTitle')}
              onChange={e => handleInputChange(e)}
              value={lotTitle}
            />
            {get(validation, 'lotTitle') && (
            <p className="color-danger my-0">{get(validation, 'lotTitle')}</p>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="lotDescription">Item Description*</Label>
            <Input
              id="lotDescription"
              className={get(validation, 'lotDescription') ? 'is-invalid' : ''}
              name="lotDescription"
              rows="12"
              type="textarea"
              onBlur={() => validateFields('lotDescription')}
              onChange={e => handleInputChange(e)}
              value={lotDescription}
            />
            {get(validation, 'lotDescription') && (
            <p className="color-danger my-0">{get(validation, 'lotDescription')}</p>
            )}
          </FormGroup>

          <div className="mt-5">
            <h3>Dimensions</h3>
          </div>

          <FormGroup>
            <Label for="dimensions">Item Dimensions</Label>
            <p className="d-inline"> (please include unit of measure)</p>
            <Input
              id="dimensions"
              name="dimensions"
              onChange={e => handleInputChange(e)}
              value={dimensions}
            />
          </FormGroup>
          <FormGroup className="mt-5">
            <Label for="shipLength">Shipping Length</Label>
            <p className="d-inline mr-2"> (inches / feet)</p>
            <FontAwesomeIcon id="shipLengthTooltip" className="color-blue" icon="question-circle" />
            <UncontrolledTooltip
              className="lot-form-tooltip"
              placement="right"
              target="shipLengthTooltip"
            >
              {`${shippingTooltipBeginText} length ${shippingTooltipEndText}`}
            </UncontrolledTooltip>
            <Input
              id="shipLength"
              className={get(validation, 'shipLength') ? 'is-invalid' : ''}
              name="shipLength"
              onBlur={() => validateFields('shipLength')}
              onChange={e => handleInputChange(e)}
              value={shipLength}
            />
            {get(validation, 'shipLength') && (
            <p className="color-danger my-0">{get(validation, 'shipLength')}</p>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="shipWidth">Shipping Width</Label>
            <p className="d-inline mr-2"> (inches / feet)</p>
            <FontAwesomeIcon id="shipWidthTooltip" className="color-blue" icon="question-circle" />
            <UncontrolledTooltip
              className="lot-form-tooltip"
              placement="right"
              target="shipWidthTooltip"
            >
              {`${shippingTooltipBeginText} width ${shippingTooltipEndText}`}
            </UncontrolledTooltip>
            <Input
              id="shipWidth"
              className={get(validation, 'shipWidth') ? 'is-invalid' : ''}
              name="shipWidth"
              onBlur={() => validateFields('shipWidth')}
              onChange={e => handleInputChange(e)}
              value={shipWidth}
            />
            {get(validation, 'shipWidth') && (
            <p className="color-danger my-0">{get(validation, 'shipWidth')}</p>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="shipHeight">Shipping Height</Label>
            <p className="d-inline mr-2"> (inches / feet)</p>
            <FontAwesomeIcon id="shipHeightTooltip" className="color-blue" icon="question-circle" />
            <UncontrolledTooltip
              className="lot-form-tooltip"
              placement="right"
              target="shipHeightTooltip"
            >
              {`${shippingTooltipBeginText} height ${shippingTooltipEndText}`}
            </UncontrolledTooltip>
            <Input
              id="shipHeight"
              className={get(validation, 'shipHeight') ? 'is-invalid' : ''}
              name="shipHeight"
              onBlur={() => validateFields('shipHeight')}
              onChange={e => handleInputChange(e)}
              value={shipHeight}
            />
            {get(validation, 'shipHeight') && (
            <p className="color-danger my-0">{get(validation, 'shipHeight')}</p>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="shipWeight">Total Weight</Label>
            <p className="d-inline mr-2"> (pounds / ounces)</p>
            <FontAwesomeIcon id="shipWeightTooltip" className="color-blue" icon="question-circle" />
            <UncontrolledTooltip
              className="lot-form-tooltip"
              placement="right"
              target="shipWeightTooltip"
            >
              This field is for the total weight of the package, including both the item and any
              packaging.
            </UncontrolledTooltip>
            <Input
              id="shipWeight"
              className={get(validation, 'shipWeight') ? 'is-invalid' : ''}
              name="shipWeight"
              onBlur={() => validateFields('shipWeight')}
              onChange={e => handleInputChange(e)}
              value={shipWeight}
            />
            {get(validation, 'shipWeight') && (
            <p className="color-danger my-0">{get(validation, 'shipWeight')}</p>
            )}
          </FormGroup>
        </Col>

        {/* Start Right Column */}
        <Col xs={6}>
          <h3>Pricing Information</h3>
          {type !== 'bin' && (
          <Fragment>
            <FormGroup>
              <Label for="loEst">Low Price Estimate</Label> {numbersOnlyText}
              <Input
                id="loEst"
                className={get(validation, 'loEst') ? 'is-invalid' : ''}
                name="loEst"
                onBlur={() => validateFields('loEst')}
                onChange={e => handleInputChange(e)}
                value={loEst}
              />
              {get(validation, 'loEst') && (
              <p className="color-danger my-0">{get(validation, 'loEst')}</p>
              )}
            </FormGroup>
            <FormGroup>
              <Label for="hiEst">High Price Estimate</Label> {numbersOnlyText}
              <Input
                id="hiEst"
                className={get(validation, 'hiEst') ? 'is-invalid' : ''}
                name="hiEst"
                onBlur={() => validateFields('hiEst')}
                onChange={e => handleInputChange(e)}
                value={hiEst}
              />
              {get(validation, 'hiEst') && (
              <p className="color-danger my-0">{get(validation, 'hiEst')}</p>
              )}
            </FormGroup>
          </Fragment>
          )}
          <FormGroup>
            <Label for={priceLabel}>{isBin ? 'Price*' : 'Starting Bid'}</Label> {numbersOnlyText}
            {hasOrderBids && (
            <FontAwesomeIcon
              id="startingBidIcon"
              className="color-blue ml-2"
              icon="dollar-sign"
            />
            )}
            <Input
              id={priceLabel}
              className={startingBidClass}
              name={priceLabel}
              disabled={hasOrderBids}
              onBlur={() => validateFields(priceLabel)}
              onChange={e => handleInputChange(e)}
              value={isBin ? itemPrice : startingBid}
            />
            {hasOrderBids && (
            <UncontrolledTooltip placement="top" target="startingBidIcon">
              {orderBidTooltipText}
            </UncontrolledTooltip>
            )}
            {get(validation, priceLabel) && (
            <p className="color-danger my-0">{get(validation, priceLabel)}</p>
            )}
          </FormGroup>
          {isBin && (
          <Fragment>
            <FormGroup>
              <Label for="shipping">Shipping</Label> (Domestic)
              <Input
                id="shipping"
                className={get(validation, 'shipping') ? 'is-invalid' : ''}
                name="shipping"
                onBlur={() => validateFields('shipping')}
                onChange={e => handleInputChange(e)}
                value={shipping}
              />
              {get(validation, 'shipping') && (
              <p className="color-danger my-0">{get(validation, 'shipping')}</p>
              )}
            </FormGroup>

            <FormGroup>
              <Label for="shippingIntl">Shipping</Label> (International)
              <Input
                id="shippingIntl"
                className={get(validation, 'shippingIntl') ? 'is-invalid' : ''}
                name="shippingIntl"
                onBlur={() => validateFields('shippingIntl')}
                onChange={e => handleInputChange(e)}
                value={shippingIntl}
              />
              {get(validation, 'shippingIntl') && (
              <p className="color-danger my-0">{get(validation, 'shippingIntl')}</p>
              )}
            </FormGroup>
          </Fragment>
          )}
          {type.toLowerCase() === 'timed' && (
          <FormGroup>
            <Label for="reserve">Reserve</Label> {numbersOnlyText}
            {hasOrderBids && (
            <FontAwesomeIcon
              id="reserveIcon"
              className="color-blue ml-2"
              icon="dollar-sign"
            />
            )}
            <Input
              id="reserve"
              className={reserveClass}
              name="reserve"
              disabled={hasOrderBids}
              onBlur={() => validateFields('reserve')}
              onChange={e => handleInputChange(e)}
              value={reserve}
            />
            {hasOrderBids && (
            <UncontrolledTooltip placement="top" target="reserveIcon">
              {orderBidTooltipText}
            </UncontrolledTooltip>
            )}
            {get(validation, 'reserve') && (
            <p className="color-danger my-0">{get(validation, 'reserve')}</p>
            )}
          </FormGroup>
          )}
          <h3 className="mt-5">Item Information</h3>
          <FormGroup>
            <Label for="artistName">Artist&#39;s Name</Label>
            <Input
              id="artistName"
              name="artistName"
              onChange={e => handleInputChange(e)}
              value={artistName}
            />
          </FormGroup>
          <FormGroup>
            <Label for="medium">Medium</Label>
            <Input
              id="medium"
              name="medium"
              onChange={e => handleInputChange(e)}
              value={medium}
            />
          </FormGroup>
          <FormGroup>
            <Label for="condition">Condition</Label>
            <Input
              id="condition"
              name="condition"
              rows="4"
              type="textarea"
              onChange={e => handleInputChange(e)}
              value={condition}
            />
          </FormGroup>
          <FormGroup>
            <Label for="circa">Circa</Label>
            <Input id="circa" name="circa" onChange={e => handleInputChange(e)} value={circa} />
          </FormGroup>
          <FormGroup>
            <Label for="provenance">Provenance</Label>
            <Input
              id="provenance"
              name="provenance"
              onChange={e => handleInputChange(e)}
              value={provenance}
            />
          </FormGroup>
          <FormGroup>
            <Label for="literature">Literature</Label>
            <Input
              id="literature"
              name="literature"
              rows="4"
              type="textarea"
              onChange={e => handleInputChange(e)}
              value={literature}
            />
          </FormGroup>
          <FormGroup>
            <Label for="exhibited">Exhibited</Label>
            <Input
              id="exhibited"
              name="exhibited"
              rows="4"
              type="textarea"
              onChange={e => handleInputChange(e)}
              value={exhibited}
            />
          </FormGroup>
          <FormGroup>
            <Label for="notes">Other Notes</Label>
            <Input
              id="notes"
              name="notes"
              rows="4"
              type="textarea"
              onChange={e => handleInputChange(e)}
              value={notes}
            />
          </FormGroup>
        </Col>
      </Row>
    </Form>
  );
};

export default LotForm;
